// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module StatsCard
 * @package @geenius/analytics/react
 * @description Renders the React summary-stat card used across analytics page
 * headers and dashboard compositions. The component combines an optional icon,
 * headline value, and trend indicator into a compact card surface.
 */

import { Badge, Button, Card, CardContent } from "@geenius/ui/react";
import type React from "react";

const SPARKLINE_WIDTH = 120;
const SPARKLINE_HEIGHT = 36;

function getSparklinePoints(values: number[]): string | null {
  const finiteValues = values.filter((value) => Number.isFinite(value));
  if (finiteValues.length < 2) return null;

  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  const range = max - min || 1;
  const lastIndex = finiteValues.length - 1;

  return finiteValues
    .map((value, index) => {
      const x = (index / lastIndex) * SPARKLINE_WIDTH;
      const y = SPARKLINE_HEIGHT - ((value - min) / range) * SPARKLINE_HEIGHT;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

/**
 * Props for rendering the React stats card.
 *
 * @property label Human-readable label for the metric being displayed.
 * @property value Formatted or raw metric value to display prominently.
 * @property icon Optional icon node shown beside the metric content.
 * @property change Optional change string rendered below the main value.
 * @property trend Optional direction token used for the change indicator.
 * @property sparkline Optional numeric series rendered as a compact trend line.
 * @property sparklineLabel Accessible label for the sparkline image.
 * @property error Optional metric-level error message or object.
 * @property onRetry Optional retry handler rendered when an error is present.
 * @property className Optional class name merged into the card wrapper.
 */
export interface StatsCardProps {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  change?: string;
  trend?: "up" | "down" | "neutral";
  sparkline?: number[];
  sparklineLabel?: string;
  error?: Error | string | null;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders the React summary-stat card.
 *
 * @param props Metric label, value, and optional trend presentation props.
 * @returns A card element showing the supplied summary metric.
 */
export function StatsCard({
  label,
  value,
  icon,
  change,
  error,
  onRetry,
  trend,
  sparkline,
  sparklineLabel,
  className,
}: StatsCardProps): React.ReactElement {
  const trendVariant =
    trend === "up" ? "success" : trend === "down" ? "danger" : "secondary";
  const trendLabel =
    trend === "up" ? "increased" : trend === "down" ? "decreased" : "changed";
  const sparklinePoints = getSparklinePoints(sparkline ?? []);

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <div className="grid gap-3" role="alert">
            <span className="text-sm font-medium text-foreground">{label}</span>
            <span className="text-sm text-muted-foreground">
              {getErrorMessage(error)}
            </span>
            {onRetry && (
              <Button
                className="w-fit"
                type="button"
                variant="secondary"
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="flex items-start gap-3 pt-6" data-analytics-stat>
        {icon && (
          <div className="text-xl" data-analytics-stat-icon>
            {icon}
          </div>
        )}
        <div
          className="flex min-w-0 flex-1 flex-col gap-1"
          data-analytics-stat-content
        >
          <span
            className="text-sm text-muted-foreground"
            data-analytics-stat-label
          >
            {label}
          </span>
          <span
            className="text-2xl font-semibold tracking-tight"
            data-analytics-stat-value
          >
            {value}
          </span>
          {change && (
            <Badge size="sm" variant={trendVariant}>
              <span
                data-analytics-stat-change
                data-trend={trend || "neutral"}
                role="img"
                aria-label={`${trendLabel} ${change}`}
              >
                <span aria-hidden="true">
                  {trend === "up" ? "↑" : trend === "down" ? "↓" : ""}
                </span>{" "}
                {change}
              </span>
            </Badge>
          )}
          {sparklinePoints ? (
            <svg
              className="mt-2 h-9 w-full max-w-32 text-primary"
              data-analytics-stat-sparkline
              viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
              role={sparklineLabel ? "img" : undefined}
              aria-label={sparklineLabel}
              aria-hidden={sparklineLabel ? undefined : true}
              focusable="false"
            >
              <polyline
                points={sparklinePoints}
                vectorEffect="non-scaling-stroke"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
